<template>
    <v-container fluid :style="{ width: $vuetify.display.lgAndUp ? '60%' : '100%', paddingTop: '50px' }">
        <v-row class="pl-7">
            <v-col sm="12">
                <!-- Logged in state -->
                <v-row v-if="loggedIn && permissions.length > 0">
                    <v-btn small color="primary" @click="logout">{{ $t('login.button.logout') }}</v-btn>
                </v-row>

                <!-- Not logged in state -->
                <v-row v-else>
                    <!-- Horizontal buttons at the top, similar to VETdata website -->
                    <v-row class="mb-6" align="start" justify="end">
                        <v-col cols="12" sm="auto" class="d-flex justify-center justify-sm-end">
                            <v-tooltip location="bottom" color="secondary">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" class="mx-2" color="primary" @click="handleLoginTotp">
                                        {{ $t('login.button.login') }} Authenticator
                                    </v-btn>
                                </template>
                                <div>
                                    <!-- eslint-disable vue/no-v-html -->
                                    <p v-html="$t('login.description.login.totp')" />
                                </div>
                            </v-tooltip>
                        </v-col>

                        <v-col cols="12" sm="auto" class="d-flex justify-center justify-sm-end">
                            <v-tooltip location="bottom" color="secondary">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" class="mx-2" color="primary" @click="handleLoginSms"> {{ $t('login.button.login') }} SMS </v-btn>
                                </template>
                                <div>
                                    <!-- eslint-disable vue/no-v-html -->
                                    <p v-html="$t('login.description.login.sms')" />
                                </div>
                            </v-tooltip>
                        </v-col>

                        <v-col cols="12" sm="auto" class="d-flex justify-center justify-sm-end">
                            <v-tooltip location="bottom" color="secondary">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" class="mx-2" color="primary" :to="{ name: 'Registration' }" link>
                                        {{ $t('login.button.registration') }}
                                    </v-btn>
                                </template>
                                <div>
                                    <p v-text="$t('login.description.registration')" />
                                </div>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                    <v-col v-if="$te('login.description.2fa.text')" cols="12">
                        <!-- eslint-disable vue/no-v-html -->
                        <span v-html="$t('login.description.2fa.text')" />&nbsp;
                        <span v-html="$t('login.description.2fa.link')" />
                    </v-col>

                    <!-- VETdata information and image placeholder in two-column layout -->
                    <v-col cols="12" class="mt-4">
                        <v-row>
                            <v-col cols="12" md="6" class="d-flex flex-column justify-start align-start">
                                <div>
                                    <h1 class="mb-3" v-text="$t('login.subtitle')" />
                                    <p style="font-size: 20px; font-weight: 300; line-height: 1.6">
                                        {{ $t('login.description.one') }}
                                        {{ $t('login.description.two') }}
                                    </p>
                                </div>
                                <v-btn class="mt-4" style="width: 200px" small color="secondary" @click="forwardToVetdata"> www.vetdata.ch </v-btn>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-img
                                    style="
                                        width: 100%;
                                        height: auto;
                                        border: 1px solid #ccc;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        background-color: #f5f5f5;
                                    "
                                    src="@/assets/vetdata_animals.webp"
                                ></v-img>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts">
import { Component, Watch, Vue, Prop } from 'vue-facing-decorator';

import { namespace } from 'vuex-facing-decorator';

import { NAME as NAME_SECURITY } from '@/store/modules/security';
const securityModul = namespace(NAME_SECURITY);

import { NAME as NAME_GENERAL } from '@/store/modules/general';
const generalModul = namespace(NAME_GENERAL);

import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
const headerNotificationModul = namespace(NAME_HEADERNOTIFICATION);

import { ISLOGGEDIN, PERMISSIONS } from '@/store/modules/security/getters';
import { LOGIN, LOGOUT, GET_BENUTZERINFO, GET_PERMISSIONS, LoginType } from '@/store/modules/security/actions';
import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';
import { NEW_ALERT, NEW_INFO } from '@/store/modules/headerNotification/actions';

enum AutoLogin {
    undefined,
    totp,
    sms
}

@Component
export default class Login extends Vue {
    private returnUrl = '/';
    private autologin = AutoLogin.undefined;

    @securityModul.Action(LOGIN) login!: Function;
    @securityModul.Action(LOGOUT) logout!: Function;
    @securityModul.Action(GET_BENUTZERINFO) getBenutzerInfo!: Function;
    @securityModul.Action(GET_PERMISSIONS) getPermissions!: Function;
    @securityModul.Getter(ISLOGGEDIN) loggedIn!: boolean;
    @securityModul.Getter(PERMISSIONS) permissions!: Array<string>;

    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @headerNotificationModul.Action(NEW_ALERT) newAlert!: Function;
    @headerNotificationModul.Action(NEW_INFO) newInfo!: Function;

    created() {
        // get return url from route parameters or default to '/'
        this.returnUrl = typeof this.$route.query.returnUrl === 'string' ? this.$route.query.returnUrl : '/';

        if (typeof this.$route.query.autologin === 'string') {
            if (this.$route.query.autologin.toLowerCase() === 'totp') {
                this.autologin = AutoLogin.totp;
            } else if (this.$route.query.autologin.toLowerCase() === 'sms') {
                this.autologin = AutoLogin.sms;
            }
        }

        if (this.loggedIn) {
            this.$router.push(this.returnUrl);
        }
        this.setSubTitle('Login');
        this.setFooterTitle('S010');
    }

    mounted() {
        if (!this.loggedIn && this.autologin != AutoLogin.undefined) {
            if (this.autologin === AutoLogin.totp) {
                this.handleLoginTotp();
            } else if (this.autologin === AutoLogin.sms) {
                this.handleLoginSms();
            }
        }
    }

    handleLoginSms() {
        this.login(LoginType.sms);
    }
    handleLoginTotp() {
        this.login(LoginType.totp);
    }

    forwardToVetdata() {
        location.replace('https://www.vetdata.ch');
    }
}
</script>

<style></style>
